import { useState } from "react";
import {
  Card,
  BlockStack,
  Badge,
  Banner,
  Button,
  DataTable,
  InlineStack,
  Spinner,
  Text,
} from "@shopify/polaris";
import { api, ApiRequestError } from "../api";
import type { DeliverySourceScan } from "../types";
import { describePromiseSource, describePromiseKind } from "../lib/promise-source";

// The scan walks real orders, so it is not run on page load — a merchant who
// never touches this panel should not cost ten Shopify round trips per visit.
export default function DeliverySourcesCard(): JSX.Element {
  const [scan, setScan] = useState<DeliverySourceScan | null>(null);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function runScan(refresh: boolean): Promise<void> {
    setScanning(true);
    setError(null);
    try {
      setScan(await api.get<DeliverySourceScan>(`/delivery-sources/scan${refresh ? "?refresh=1" : ""}`));
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.body.message : "Could not scan orders");
    } finally {
      setScanning(false);
    }
  }

  return (
    <Card>
      <BlockStack gap="300">
        <Text as="h2" variant="headingMd">
          Delivery Sources
        </Text>
        <Text as="p" tone="subdued">
          ShipPromise reads a delivery deadline from whichever of these your orders actually carry,
          taking the first one it finds. Scanning your recent orders shows which of them are
          populated, so you can tell whether a date you set is reaching the rate picker. Any
          date-carrying metafield the scan finds is picked up automatically. There is nothing to
          configure here.
        </Text>

        {error && <Banner tone="critical">{error}</Banner>}

        <InlineStack gap="200" blockAlign="center">
          <Button onClick={() => void runScan(scan !== null)} loading={scanning}>
            {scan ? "Rescan orders" : "Scan recent orders"}
          </Button>
          {scan && (
            <Text as="span" tone="subdued" variant="bodySm">
              {scan.order_count} order{scan.order_count === 1 ? "" : "s"} scanned{" "}
              {new Date(scan.scanned_at).toLocaleString()}
            </Text>
          )}
        </InlineStack>

        {scanning && !scan ? <Spinner accessibilityLabel="Scanning orders" size="small" /> : null}

        {scan && scan.sources.length === 0 && (
          <Banner tone="warning">
            None of the {scan.order_count} orders scanned carry a delivery date in any field ShipPromise
            reads. Rates are being chosen on price alone unless a rule says otherwise.
          </Banner>
        )}

        {scan && scan.sources.length > 0 && (
          <DataTable
            columnContentTypes={["text", "text", "numeric", "numeric"]}
            headings={["Where", "Field", "Orders with it", "Holding a readable date"]}
            rows={scan.sources.map((source) => [
              describePromiseKind(source.kind),
              source.key,
              String(source.present),
              source.parseable === source.present
                ? String(source.parseable)
                : `${source.parseable} of ${source.present}`,
            ])}
          />
        )}

        {scan && scan.resolved.length > 0 && (
          <BlockStack gap="150">
            <Text as="h3" variant="headingSm">
              What would actually decide these orders
            </Text>
            <Text as="p" tone="subdued" variant="bodySm">
              A field lower down the list is never read when one above it is populated, so this is
              the count that matters.
            </Text>
            <InlineStack gap="150" wrap>
              {scan.resolved.map((entry) => (
                <Badge key={entry.source}>{`${describePromiseSource(entry.source)}: ${entry.orders}`}</Badge>
              ))}
            </InlineStack>
          </BlockStack>
        )}
      </BlockStack>
    </Card>
  );
}
